import { Button } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import { Link, useLocation } from 'react-router-dom';

import { ApiError, errorMessage } from '@/domains/api';
import { authApi } from '../api';
import { useAuth } from '../auth-context';
import { HOME_ROUTE, LOGIN_ROUTE } from '../constants';
import { tokenFromLink } from '../link-token';
import { AuthShell } from './auth-shell';
import { DoorBackLink } from './door-back-link';
import { LinkCodeField } from './link-code-field';

type Outcome = 'ready' | 'accepting' | 'accepted' | 'refused' | 'failed';

/**
 * `/invitations/accept` — where survey-api's point-of-contact invitation links (SR-416, its
 * B5-16): `POST /auth/invitations/acceptance { token }`, anonymous. A TEAMS or RIA_FIRM
 * response that names an office email invites that address to act for the participant; accepting
 * links the account signing in with it as the participant's point of contact, and makes that
 * account where none exists.
 *
 * **The page cannot say whose invitation it is.** No route reads an invitation by its token, so
 * the participant and the address are the mail's to name, and the page says what an invitation
 * is in general. The token is read as `/email/verify` reads it — fragment first, the query second,
 * never stripped — and kept in an editable box (`LinkCodeField`).
 *
 * **It accepts on a press, never on load**, for `/email/verify`'s reason: a mail's link scanners
 * run the page.
 *
 * **No password** (the IdP sign-in design, decision 9): the account is made or linked as it is,
 * and one that does not sign in yet gets an invitation to sign in at Invent at the address this
 * link was mailed to — the one the token proved. The accepted screen says so, and offers sign-in
 * for an account that signs in already. Every token that cannot be used is one empty 400 and one
 * sentence.
 *
 * **No session is made or dropped.** A 204 issues none: signed out, the way on is the sign-in
 * screen; signed in, the account is read again (`refreshUser` — the invited address is confirmed
 * by the acceptance, and it may be this account's) and the way on is `/`.
 */
export function AcceptInvitationPage() {
  const { hash, search } = useLocation();
  const { isAuth, refreshUser } = useAuth();
  const [outcome, setOutcome] = React.useState<Outcome>('ready');
  /** survey-api's own words for a 4xx none of the outcomes names — a 429, say; `null` for an
   *  answer that never arrived (no `ApiError`, or a 5xx), which proves nothing either way. */
  const [refusal, setRefusal] = React.useState<Nullable<string>>(null);

  // Fragment first, query second — `tokenFromLink` says why.
  const [token, setToken] = React.useState(() => tokenFromLink(hash, search).trim());

  const accept = async (event: React.FormEvent) => {
    event.preventDefault();
    const code = token.trim();
    if (!code) return;
    setOutcome('accepting');
    try {
      await authApi.acceptInvitation(code);
      setOutcome('accepted');
      await refreshUser();
    } catch (error) {
      if (error instanceof ApiError && error.status === 400) {
        setOutcome('refused');
      } else {
        setRefusal(
          error instanceof ApiError && error.status < 500
            ? `The system refused this: ${errorMessage(error, 'no reason given')}`
            : null
        );
        setOutcome('failed');
      }
    }
  };

  const title = titleOf(outcome);

  return (
    <AuthShell title={title}>
      <h1 className="text-2xl text-[var(--color-royal-blue)]">{title}</h1>
      {outcome === 'accepted' ? (
        <>
          <p className="mt-2 text-sm text-muted-foreground">
            You now act for them at Veritant. The link is used up and will not work again.
          </p>
          <p className="mt-3 mb-6 text-sm text-muted-foreground">
            If your account does not sign in yet, an invitation to sign in at Invent goes to the
            address this link was mailed to.
          </p>
          <Button asChild className="w-full">
            <Link to={isAuth ? HOME_ROUTE : LOGIN_ROUTE}>{isAuth ? 'Continue' : 'Sign in'}</Link>
          </Button>
        </>
      ) : outcome === 'refused' ? (
        <>
          <p role="alert" className="mt-2 mb-6 text-sm text-muted-foreground">
            It was used already, it expired, or Veritant withdrew it or sent a newer one. A newer
            invitation is mailed to the same address.
          </p>
          <DoorBackLink isAuth={isAuth} />
        </>
      ) : (
        <>
          <p className="mt-2 mb-6 text-sm text-muted-foreground">
            This link was mailed to a point of contact a survey named. Accept it to sign in for
            them at Veritant.
          </p>
          <form noValidate className="grid gap-5" onSubmit={(event) => void accept(event)}>
            <LinkCodeField
              label="Invitation code"
              value={token}
              onChange={setToken}
              disabled={outcome === 'accepting'}
            />

            {outcome === 'failed' && (
              // Another 4xx is said in survey-api's words; an answer that never arrived proves
              // nothing about the token, so the press stays — an accepted one answers that it
              // cannot be used next time.
              <p role="alert" className="text-sm font-medium text-destructive">
                {refusal ?? (
                  <>
                    The answer never arrived — the invitation may or may not be accepted. Press again:
                    a link already used answers that it cannot be used.
                  </>
                )}
              </p>
            )}
            <Button
              type="submit"
              className="w-full"
              disabled={outcome === 'accepting' || !token.trim()}
            >
              {outcome === 'accepting' ? 'Accepting…' : 'Accept the invitation'}
            </Button>
          </form>
          <DoorBackLink isAuth={isAuth} />
        </>
      )}
    </AuthShell>
  );
}

function titleOf(outcome: Outcome): string {
  if (outcome === 'accepted') return 'The invitation is accepted';
  if (outcome === 'refused') return 'This link cannot be used';
  return 'Accept the invitation';
}
